import { useCallback, useState } from "react";

import { SortableTreeWithoutDndContext } from "@nosferatu500/react-sortable-tree";
import FontAwesome from "../../utilities/FontAwesome";
import CleverSiteTreeTheme from "./CleverSiteTreeTheme";

import styles from "./styles/tree.module.scss";
import classNames from "classnames";

import { observer } from "mobx-react";
import { toJS } from "mobx";
import { useMachine } from "@xstate/react";
import { FetchMachine } from "../../machines/FetchMachine";
import { convertTreeToFlat } from "../../../utils/DataUtilities";
import {
  isIconLink,
  isLink,
  isModalLink,
  isSecondLevelListHeading,
  isTopLevel,
  isTopLevelIconHeading,
  isTopLevelListHeading,
} from "../../modals/AdminModal/utilities";
import { conditionType } from "../../modals/AdminModal/AdminModalTypes";
import ApiClient, { ApiError } from "../../../backends/ApiClient";
import NotificationManager from "../../notifications/NotificationManager";
import ClickableLink from "../../utilities/ClickableLink";
import { useStores } from "../../../stores/useStores";
import {
  Badge,
  Flex,
  IconButton,
  Popover,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { reportError } from "../../../utils/errorReporting";

const treeNodeClassName = classNames({
  [styles.treeNode]: true,
});

const SubtitleTextOptions = {
  "1_36": "List with Headings",
  "2_36": "Icons + Description",
  "2_40": "Traditional Link",
  "1_40": "Heading",
  "3_44": "Link Under Heading",
  "3_36": "Traditional Link",
  "3_40": "Traditional Link",
  "4_36": "Modal Trigger",
  "4_40": "Modal Trigger",
  "4_44": "Modal Trigger",
};

export const canDropNode = (node) => {
  return (
    (isTopLevel(node.node) && !node.nextParent) || // if at top level, can move to top level
    // if heading, parent must be type 1 and have 36char length
    (isSecondLevelListHeading(node.node) &&
      node.nextParent &&
      isTopLevelListHeading(node.nextParent)) || // allowed if top level, under a secondary heading, or under an icon top heading
    (isLink(node.node) &&
      (!node.nextParent ||
        isSecondLevelListHeading(node.nextParent) ||
        isTopLevelIconHeading(node.nextParent)))
  );
};

export function getUpdatedItemPathsFromMove<
  Item extends { path: string; id: string | number },
>(previousFlatState: Item[], nextFlatState: Item[]): Item[] {
  const updatedItems: Item[] = [];

  // find all items whose paths have changed and therefore need to be updated
  const nextById = nextFlatState.reduce((acc, curr) => {
    acc[curr.id] = curr;
    return acc;
  }, {});
  previousFlatState.forEach((n) => {
    let nextItem = nextById[n.id];
    if (nextItem.path !== n.path) {
      updatedItems.push(nextItem);
    }
  });

  return updatedItems;
}

export function numExpandedNodes(tree) {
  let n = 0;
  tree.forEach((t) => {
    n++;
    if (t.expanded) {
      t.children.forEach((c) => {
        n += numExpandedNodes([c]);
      });
    }
  });
  return n;
}

export function mergeNavigationLists(currentList, newList) {
  const merged = new Map();
  currentList.forEach((c) => {
    merged.set(c.id, c);
  });
  newList.forEach((n) => {
    merged.set(n.id, {
      ...n,
      expanded: merged.get(n.id).expanded,
    });
  });
  return Array.from(merged.values()).sort((a, b) => (a.path < b.path ? -1 : 1));
}

function applyValueToChildren(
  allNodes: Navigation[],
  parentNode: Navigation,
  alteration: Partial<Navigation>,
): Navigation[] {
  return allNodes
    .filter(
      (item) => item.path.slice(0, parentNode.path.length) === parentNode.path,
    )
    .map((item) => {
      return { ...item, ...alteration };
    });
}

const DRAFT_LINK_MESSAGE = "Leads to a page in draft, so it was not turned on.";

function safeGetErrorDetail(e): string {
  return e instanceof ApiError
    ? ((e.data as { detail?: string })?.detail as string)
    : "Something went wrong. Please try again.";
}

const Tree = observer(
  (props: {
    toggleModal: (condition: conditionType, node: Navigation) => void;
    isTouch?: boolean;
  }) => {
    const { organizationStore } = useStores();
    const [height, setHeight] = useState(
      numExpandedNodes(organizationStore.navigationTree) * 60 + 20,
    );
    const [brokenLinkMessageByNodeId, setBrokenLinkMessageByNodeId] = useState<
      Record<number, string>
    >({});
    const [pendingNodeIds, setPendingNodeIds] = useState<Set<number>>(
      new Set(),
    );
    const [current, send] = useMachine(FetchMachine);

    const toggleEnabled = useCallback(
      async (node) => {
        const isEnabling = !node.enabled;
        setPendingNodeIds((prev) => new Set(prev).add(node.id));
        try {
          const client = await ApiClient();
          const params = {
            path: {
              organization_pk: organizationStore.organization.id,
              id: node.id,
            },
          };
          const responseData = !isEnabling
            ? await client.POST(
                "/organizations/{organization_pk}/navigations/{id}/disable/",
                { params },
              )
            : await client.POST(
                "/organizations/{organization_pk}/navigations/{id}/enable/",
                { params },
              );
          // the spec types this response as a single Navigation object, but the endpoint
          // actually returns the full updated navigation list, matching the old code's behavior (SBSB-2912)
          // is_draft_page is set by the enable endpoint only and is not in the generated spec
          const updated = responseData as unknown as (Navigation & {
            is_draft_page?: boolean;
          })[];
          const skipped = updated.filter((n) => n.is_draft_page);
          organizationStore.navigation = mergeNavigationLists(
            organizationStore.navigation,
            updated,
          );
          setBrokenLinkMessageByNodeId((prev) => {
            const next = { ...prev };
            // clear first, or a link since published keeps its stale warning
            for (const n of updated) {
              delete next[n.id];
            }
            for (const n of skipped) {
              next[n.id] = DRAFT_LINK_MESSAGE;
            }
            return next;
          });
          if (skipped.length) {
            const titles = skipped.map((n) => `"${n.title}"`).join(", ");
            NotificationManager.info(
              skipped.length === 1
                ? `${titles} leads to a page in draft, so it was not turned on.`
                : `${titles} lead to pages in draft, so they were not turned on.`,
              "",
              8000,
            );
          }
        } catch (e) {
          const message = safeGetErrorDetail(e);
          NotificationManager.error(message, "", 5000);
          setBrokenLinkMessageByNodeId((prev) => ({
            ...prev,
            [node.id]: message,
          }));
        } finally {
          setPendingNodeIds((prev) => {
            const next = new Set(prev);
            next.delete(node.id);
            return next;
          });
        }
      },
      [organizationStore.navigation],
    );

    const handleMoveNode = useCallback(
      async (obj: {
        node: NavigationInTree;
        nextParentNode: NavigationInTree | null;
        treeData: NavigationInTree[];
      }) => {
        // when an icon link is dragged to become a traditional link, or visa versa, we need to update its type
        if (
          isIconLink(obj.node) &&
          (!obj.nextParentNode || isSecondLevelListHeading(obj.nextParentNode))
        ) {
          obj.node.type = 3;
        } else if (
          !isIconLink(obj.node) &&
          !isModalLink(obj.node) &&
          obj.nextParentNode &&
          isTopLevelIconHeading(obj.nextParentNode)
        ) {
          obj.node.type = 2;
        }

        // remove the district link when editing
        const previousFlatState = toJS(organizationStore.navigation).filter(
          (n) => !n.is_district_link,
        );
        const nextFlatState = convertTreeToFlat(
          obj.treeData,
          organizationStore.organization.id.replaceAll("-", ""),
        );
        let updatedItems = getUpdatedItemPathsFromMove<Navigation>(
          previousFlatState,
          nextFlatState,
        );

        if (obj.nextParentNode && !obj.nextParentNode.enabled) {
          // if the next parent is disabled, disable all children
          updatedItems = applyValueToChildren(
            updatedItems,
            obj.nextParentNode,
            {
              enabled: false,
            },
          );
        }

        // no need to hit the API with an empty list
        if (updatedItems.length === 0) return;

        /*
        this removes having to wait for the API to update the state. It's a bit of a trade-off, because this introduces the possibility for
        errors if the user ends another drag before the API has time to respond. Also add back the district link that we've possibly
        removed if we're at a school.
         */
        organizationStore.navigation = [
          ...toJS(organizationStore.navigation).filter(
            (n) => n.is_district_link,
          ),
          ...nextFlatState,
        ];

        send("FETCH");
        try {
          const client = await ApiClient();
          const updated = await client.PUT(
            "/organizations/{organization_pk}/navigations/bulk_update/",
            {
              params: {
                path: { organization_pk: organizationStore.organization.id },
              },
              body: updatedItems as never,
            },
          );
          organizationStore.navigation = mergeNavigationLists(
            organizationStore.navigation,
            updated,
          );
          send("FULFILL");
        } catch (error) {
          reportError(error, { error_source: "handled" });
          // reset to the previous state if something went wrong
          organizationStore.navigation = previousFlatState;
          send("REJECT");
        }
      },
      [],
    );

    const deleteNavigation = async (node: Navigation) => {
      const includeAllChildren = organizationStore.navigation
        .filter((item) => {
          return item.path.slice(0, node.path.length) === node.path;
        })
        .map((item) => item.id);
      // use something like ^^ for getting all children and bulk deleting
      try {
        const client = await ApiClient();
        await client.DELETE(
          "/organizations/{organization_pk}/navigations/bulk_destroy/",
          {
            params: {
              path: { organization_pk: organizationStore.organization.id },
            },
            body: includeAllChildren as never, // spec types requestBody as `never`, but the endpoint accepts an array of ids to delete (SBSB-2912)
          },
        );
        organizationStore.navigation = organizationStore._navigation.filter(
          (item) => !includeAllChildren.includes(item.id),
        );
        setBrokenLinkMessageByNodeId((prev) => {
          const next = { ...prev };
          includeAllChildren.forEach((id) => delete next[id]);
          return next;
        });
      } catch (error) {
        reportError(error, { error_source: "handled" });
        console.error(error);
      }
    };

    const getNodeProps = useCallback(
      (rowInfo) => {
        let subtitle =
          SubtitleTextOptions[
            `${rowInfo.node.type}_${rowInfo.node.path.length}`
          ];
        let nodeProps = {
          className: treeNodeClassName as string,
          buttons: null as React.ReactNode,
          title: null as React.ReactNode,
          style: null as any | undefined,
        };

        const brokenLinkMessage = brokenLinkMessageByNodeId[rowInfo.node.id];

        const isLoading = pendingNodeIds.has(rowInfo.node.id);

        const addLabel = isTopLevelListHeading(rowInfo.node)
          ? "Add Heading"
          : "Add Sublink";
        const enabledLabel = rowInfo.node.enabled ? "Disable" : "Enable";

        const editingButtons = (
          <>
            {(isTopLevelListHeading(rowInfo.node) ||
              isTopLevelIconHeading(rowInfo.node) ||
              isSecondLevelListHeading(rowInfo.node)) && (
              <IconButton
                key="add"
                variant="ghost"
                color="gray"
                onClick={() =>
                  props.toggleModal(
                    isTopLevelListHeading(rowInfo.node)
                      ? conditionType.ADD_HEADING
                      : conditionType.ADD_SUBLINK,
                    rowInfo.node,
                  )
                }
                aria-label={addLabel}
                title={addLabel}
              >
                <FontAwesome prefix={"fas"} name={"fa-plus"} />
              </IconButton>
            )}
            <IconButton
              key="edit"
              variant="ghost"
              color="gray"
              onClick={() =>
                props.toggleModal(conditionType.EDITING, rowInfo.node)
              }
              aria-label={"Edit Link"}
              title={"Edit Link"}
            >
              <FontAwesome prefix={"fas"} name={"fa-pen"} />
            </IconButton>
            <IconButton
              key="enabled"
              variant="ghost"
              color="gray"
              loading={isLoading}
              onClick={() => toggleEnabled(rowInfo.node)}
              aria-label={enabledLabel}
              title={enabledLabel}
            >
              <FontAwesome
                prefix={"fas"}
                name={rowInfo.node.enabled ? "fa-eye-slash" : "fa-eye"}
              />
            </IconButton>
            <IconButton
              key="delete"
              variant="ghost"
              color="red"
              onClick={() => {
                if (confirm("Are you sure you want to do that?")) {
                  deleteNavigation(rowInfo.node);
                }
              }}
              aria-label={"Delete"}
              title={"Delete"}
            >
              <FontAwesome prefix={"fas"} name={"fa-trash-can"} />
            </IconButton>
          </>
        );

        nodeProps.buttons = (
          <>
            {/* Mobile: Popover version */}
            <div className={styles.mobileButtonsWrapper}>
              <Popover.Root>
                <Popover.Trigger>
                  <IconButton
                    variant="ghost"
                    color="gray"
                    aria-label={"Link actions"}
                  >
                    <FontAwesome prefix={"fas"} name={"fa-ellipsis"} />
                  </IconButton>
                </Popover.Trigger>
                <Popover.Content side={"left"} align={"center"} size={"1"}>
                  <Flex gap={"3"} align={"center"}>
                    {editingButtons}
                  </Flex>
                </Popover.Content>
              </Popover.Root>
            </div>

            {/* Desktop: Direct buttons */}
            <div className={styles.desktopButtonsWrapper}>{editingButtons}</div>
          </>
        );

        const linkStatusIcon = brokenLinkMessage ? (
          <Tooltip key="brokenLink" content={brokenLinkMessage}>
            <span
              className={styles.nodeBrokenIcon}
              tabIndex={0}
              role="img"
              aria-label={brokenLinkMessage}
            >
              <FontAwesome
                ariaHidden={true}
                prefix={"fas"}
                name={"fa-exclamation-triangle"}
              />
            </span>
          </Tooltip>
        ) : null;

        const nodeTitleClassName = classNames({
          [styles.nodeTitle]: true,
          [styles.nodeDisabled]: !rowInfo.node.enabled,
        });

        const nodeSubtitleClassName = classNames({
          [styles.nodeSubtitle]: true,
          [styles.nodeDisabled]: !rowInfo.node.enabled,
        });

        const nodeIconClassName = classNames({
          [styles.nodeIcon]: true,
          [styles.nodeIconDisabled]: !rowInfo.node.enabled,
        });

        const nodeTitle =
          rowInfo.node.url && rowInfo.node.type !== 4 ? (
            <Text asChild weight="bold" truncate className={nodeTitleClassName}>
              <ClickableLink target={"_blank"} href={rowInfo.node.url}>
                {rowInfo.node.title}
              </ClickableLink>
            </Text>
          ) : (
            <Text weight="bold" truncate className={nodeTitleClassName}>
              {rowInfo.node.title}
            </Text>
          );

        nodeProps.title = (
          <span className={styles.nodeLabel}>
            {rowInfo.node.icon && (
              <FontAwesome
                key="nodeIcon"
                className={nodeIconClassName}
                ariaHidden={true}
                name={rowInfo.node.icon}
                fixedWidth
              />
            )}
            <span className={styles.nodeText}>
              {nodeTitle}
              {subtitle && (
                <Badge
                  variant="soft"
                  color="gray"
                  size="1"
                  className={nodeSubtitleClassName}
                >
                  {subtitle}
                </Badge>
              )}
            </span>
            {!rowInfo.node.enabled && (
              <FontAwesome
                key="nodeHiddenIcon"
                name={"fa-eye-slash"}
                prefix={"far"}
                className={styles.nodeHiddenIcon}
                role={"img"}
                aria-label={"Hidden"}
                title={"Hidden"}
                ariaHidden={false}
              />
            )}
            {linkStatusIcon}
          </span>
        );

        return nodeProps;
      },
      [pendingNodeIds, brokenLinkMessageByNodeId],
    );

    const treeContainer = classNames({
      [styles.container]: true,
      [styles.containerLoading]: current.value === "PENDING",
    });

    return (
      <SortableTreeWithoutDndContext
        style={{ height }}
        treeData={organizationStore.navigationTree.filter(
          (n) => !n.is_district_link,
        )}
        onChange={() => {}}
        onVisibilityToggle={({ treeData, node, expanded }) => {
          organizationStore.navigation = organizationStore.navigation.map(
            (n) => {
              if (n.id === node.id) {
                n.expanded = expanded;
              }
              return n;
            },
          );
          setHeight(numExpandedNodes(treeData) * 60 + 20);
        }}
        onMoveNode={handleMoveNode}
        theme={CleverSiteTreeTheme}
        rowHeight={60}
        scaffoldBlockPxWidth={30}
        generateNodeProps={getNodeProps}
        canDrop={canDropNode}
        getNodeKey={({ node }) => node.id}
        maxDepth={3}
        canNodeHaveChildren={(node) =>
          isTopLevelIconHeading(node) ||
          isTopLevelListHeading(node) ||
          isSecondLevelListHeading(node)
        }
        className={treeContainer}
      />
    );
  },
);

export default Tree;
